import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import ActivityForm, { type ActivityFormData } from '../components/executive-protection/ActivityForm';
import { executiveProtectionService, type Activity } from '../services/executive-protection.service';
import { useAuth } from '../stores/auth';

export default function CreateActivity() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [apiError, setApiError] = useState<string | null>(null);

  // Fetch protected persons for the form dropdown
  const { data: personsData, isLoading: isLoadingPersons } = useQuery({
    queryKey: ['executive-protection', 'persons', { page: 1, limit: 100 }],
    queryFn: () => executiveProtectionService.getProtectedPersons({ page: 1, limit: 100 }),
  });
  const protectedPersons = personsData?.protectedPersons ?? [];

  // Fetch venues for the form dropdown
  const { data: venues = [], isLoading: isLoadingVenues } = useQuery({
    queryKey: ['executive-protection', 'venues'],
    queryFn: () => executiveProtectionService.getVenues(),
  });

  // Mutation for creating activity
  const createActivityMutation = useMutation({
    mutationFn: (data: Omit<Activity, 'id' | 'createdAt' | 'updatedAt'>) =>
      executiveProtectionService.createActivity(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activities'] });
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'stats'] });
      navigate('/security/executive-protection/activities');
    },
    onError: (error: unknown) => {
      console.error('Failed to create activity:', error);
      const err = error as { message?: string; code?: string; details?: unknown };
      // Zod validation errors carry a details array of issues
      if (Array.isArray(err.details)) {
        const errorMessages = (err.details as Array<{ path?: string[]; message?: string }>)
          .map((detail) => `${detail.path?.join('.')}: ${detail.message}`)
          .join(', ');
        setApiError(`Validation error: ${errorMessages}`);
      } else if (err.message) {
        // Structured errors such as END_IN_PAST (422) carry a clear message
        setApiError(err.message);
      } else {
        setApiError('An unexpected error occurred. Please try again.');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  const handleSubmit = async (data: ActivityFormData) => {
    if (!user?.id) {
      setApiError('User must be authenticated to create activities');
      return;
    }

    setApiError(null);

    // If the user typed a new venue, create it first and use its id
    let venueId = data.venueId;
    if (data.newVenueName && data.newVenueAddress) {
      const newVenue = await executiveProtectionService.createVenue({
        name: data.newVenueName,
        address: data.newVenueAddress,
      });
      if (!newVenue) {
        setApiError('Failed to create the new venue. Please try again.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      venueId = newVenue.id;
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'venues'] });
    }

    await createActivityMutation.mutateAsync({
      protectedPersonId: data.protectedPersonId,
      protectedPersonIds: data.protectedPersonIds,
      agentIds: data.agentIds,
      leadAgentId: data.leadAgentId ?? undefined,
      activityType: data.activityType,
      title: data.title,
      description: data.description,
      startDatetime: data.startDatetime,
      endDatetime: data.endDatetime,
      location: data.location,
      locationLatitude: data.locationLatitude ?? undefined,
      locationLongitude: data.locationLongitude ?? undefined,
      venueId,
      status: 'PLANNED',
      riskAssessment: data.riskAssessment,
      securityRequirements: data.securityRequirements,
      closestHospitalName: data.closestHospitalName,
      closestHospitalAddress: data.closestHospitalAddress,
      closestHospitalPhone: data.closestHospitalPhone,
      closestHospitalDistance: data.closestHospitalDistance,
      closestHospitalTravelTime: data.closestHospitalTravelTime,
      closestHospitalCapabilities: data.closestHospitalCapabilities,
      alternativeMedicalFacilities: data.alternativeMedicalFacilities,
      evacuationRoute: data.evacuationRoute,
      onsiteMedicalSupport: data.onsiteMedicalSupport,
      customFields: data.customFields,
      // create-only payload: null collapses to absent (clearing a plan is an edit-flow concern)
      communicationPlan: data.communicationPlan ?? undefined,
      createdBy: user.id,
      allowPastEnd: data.allowPastEnd,
    });
  };

  const handleCancel = () => {
    navigate('/security/executive-protection/activities');
  };

  // Map protected persons to the format expected by ActivityForm
  const mappedPersons = protectedPersons.map((person) => ({
    id: person.id,
    firstName: person.firstName,
    lastName: person.lastName,
    title: person.title ?? '',
  }));

  // Map venues to the format expected by ActivityForm
  const mappedVenues = venues.map((venue) => ({
    id: venue.id,
    name: venue.name,
    address: venue.address ?? '',
    securityContactName: venue.securityContactName,
    securityContactPhone: venue.securityContactPhone,
  }));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  const isLoading = isLoadingPersons || isLoadingVenues;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <div className="flex items-center space-x-4 mb-6">
          <Link
            to="/security/executive-protection/activities"
            className="p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Create New Activity
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Schedule a new activity for a protected person
            </p>
          </div>
        </div>
      </motion.div>

      {/* Error Banner */}
      {apiError && (
        <motion.div
          variants={itemVariants}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-danger-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-danger-800 dark:text-danger-200">
                  Error Creating Activity
                </h3>
                <p className="mt-1 text-sm text-danger-700 dark:text-danger-300">
                  {apiError}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  type="button"
                  onClick={() => setApiError(null)}
                  className="inline-flex rounded-md bg-danger-50 dark:bg-danger-900/20 p-1.5 text-danger-500 hover:bg-danger-100 dark:hover:bg-danger-900/40 focus:outline-none focus:ring-2 focus:ring-danger-600 focus:ring-offset-2"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <motion.div variants={itemVariants}>
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-secondary-600 dark:text-secondary-400">Loading...</span>
          </div>
        </motion.div>
      ) : (
        <motion.div variants={itemVariants}>
          <ActivityForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            protectedPersons={mappedPersons}
            venues={mappedVenues}
            isLoading={createActivityMutation.isPending}
            googleMapsApiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''}
          />
        </motion.div>
      )}
    </motion.div>
  );
}
